import React, { useEffect, useMemo, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Typography,
    Box,
    Grid,
    Chip,
    Button,
    Paper,
    Collapse,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import ImageIcon from '@mui/icons-material/Image';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { getAllPostMonthlyTargetsAction, getAllPostActivitiesAction, getAllPostMediaFilesAction } from '../../store/actions';

const PostActivityDetailsView = ({ open, onClose, row }) => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const isXs = useMediaQuery(theme.breakpoints.only('xs'));

    const [expandedMonthId, setExpandedMonthId] = useState(null);
    const [openImageDialog, setOpenImageDialog] = useState(false);
    const [selectedActivityId, setSelectedActivityId] = useState(null);

    // Redux selectors
    const { successData: monthlyTargetsData, loading: monthlyTargetsLoading } = useSelector(
        (state) => state.getAllPostMonthlyTargetsReducer || {}
    );
    const { successData: activitiesData, loading: activitiesLoading } = useSelector(
        (state) => state.getAllPostActivityReducer || {}
    );
    const { successData: mediaFilesData, loading: mediaFilesLoading } = useSelector(
        (state) => state.getAllPostMediaFilesReducer || {}
    );

    // Fetch data when dialog opens
    useEffect(() => {
        if (open && row) {
            dispatch(getAllPostMonthlyTargetsAction());
            dispatch(getAllPostActivitiesAction());
        }
    }, [open, row, dispatch]);

    // Filter monthly targets
    const filteredMonthlyTargets = useMemo(() => {
        if (!row || !monthlyTargetsData) return [];
        let monthlyTargetsList = monthlyTargetsData?.data || [];
        if (!Array.isArray(monthlyTargetsList)) return [];
        return monthlyTargetsList.filter(target => {
            const targetPostTargetId = target.post_target_id || target.postTargetId;
            return targetPostTargetId === row.id;
        });
    }, [row, monthlyTargetsData]);

    if (!row) return null;

    // Get activities for a specific month
    const getActivitiesForMonth = (monthId) => {
        let activitiesList = [];
        if (activitiesData?.activities) {
            activitiesList = activitiesData.activities;
        } else if (activitiesData?.data?.activities) {
            activitiesList = activitiesData.data.activities;
        } else if (Array.isArray(activitiesData?.data)) {
            activitiesList = activitiesData.data;
        } else if (Array.isArray(activitiesData)) {
            activitiesList = activitiesData;
        }
        if (!Array.isArray(activitiesList)) return [];

        const postTargetId = Number(row.id);
        const postMonthId = Number(monthId);
        return activitiesList.filter(activity => {
            const activityPostTargetId = Number(activity.post_target_id ?? activity.postTargetId);
            const activityPostMonthId = Number(activity.post_month_id ?? activity.postMonthId);
            return activityPostTargetId === postTargetId && activityPostMonthId === postMonthId;
        });
    };

    // Image handlers
    const handleOpenImages = (activityId) => {
        setSelectedActivityId(activityId);
        setOpenImageDialog(true);
        dispatch(getAllPostMediaFilesAction({
            endPoint: `activity-media/post?activityId=${activityId}`
        }));
    };

    const handleCloseImages = () => {
        setOpenImageDialog(false);
        setSelectedActivityId(null);
    };

    const handleToggleActivities = (monthId) => {
        setExpandedMonthId(expandedMonthId === monthId ? null : monthId);
    };

    const getStatusColor = (isActive) => {
        if (isActive) {
            return { backgroundColor: '#D1FAE5', color: '#047857' };
        }
        return { backgroundColor: '#FECACA', color: '#B91C1C' };
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                fullWidth
                maxWidth="lg"
                slotProps={{ paper: { sx: { borderRadius: 3 } } }}
            >
                <DialogTitle sx={{ fontWeight: 700, fontSize: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Post-Season Activity Details
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ pt: 2 }}>
                    {/* Post Activity Details Card */}
                    <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3, position: 'relative' }}>
                        {isXs ? (
                            <Box sx={{ p: 1, minWidth: 220, minHeight: 180, position: 'relative' }}>
                                <Chip
                                    label={row?.is_active ? 'Active' : 'Inactive'}
                                    size="small"
                                    sx={{
                                        position: 'absolute',
                                        top: 12,
                                        right: 12,
                                        ...getStatusColor(row?.is_active),
                                        fontWeight: 600,
                                        fontSize: 12,
                                    }}
                                />
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        <span style={{ color: '#374151' }}>Season:</span> {row?.season?.name}{row?.season?.year ? ` (${row.season.year})` : ''}
                                    </Typography>
                                    <Typography variant="body2">
                                        <span style={{ color: '#374151' }}>Area Type:</span> {row?.target_area_type}
                                    </Typography>
                                    <Typography variant="body2">
                                        <span style={{ color: '#374151' }}>Dealer visit:</span> {row?.dv_done_count}/{row?.dv_count}
                                    </Typography>
                                    <Typography variant="body2">
                                        <span style={{ color: '#374151' }}>Field Day:</span> {row?.fd_done_count}/{row?.fd_count}
                                    </Typography>
                                    <Typography variant="body2">
                                        <span style={{ color: '#374151' }}>Field Visit:</span> {row?.fv_done_count}/{row?.fv_count}
                                    </Typography>
                                </Box>
                            </Box>
                        ) : (
                            <>
                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                    Activity Information
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Typography variant="body2" color="textSecondary">Activity ID</Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{row?.id}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Typography variant="body2" color="textSecondary">Season</Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{row?.season?.name}{row?.season?.year ? ` (${row.season.year})` : ''}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Typography variant="body2" color="textSecondary">Target Area Type</Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{row?.target_area_type}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Typography variant="body2" color="textSecondary">Dealer Visit</Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{row?.dv_done_count}/{row?.dv_count}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Typography variant="body2" color="textSecondary">Field Day</Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{row?.fd_done_count}/{row?.fd_count}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Typography variant="body2" color="textSecondary">Field Visit</Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{row?.fv_done_count}/{row?.fv_count}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Typography variant="body2" color="textSecondary">Status</Typography>
                                        <Chip
                                            label={row?.is_active ? 'Active' : 'Inactive'}
                                            size="small"
                                            sx={{
                                                mt: 0.5,
                                                ...getStatusColor(row?.is_active),
                                                fontWeight: 600
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </>
                        )}
                    </Paper>

                    {/* Monthly Targets Section */}
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        Monthly Targets
                    </Typography>

                    {monthlyTargetsLoading ? (
                        <Typography>Loading monthly targets...</Typography>
                    ) : filteredMonthlyTargets.length > 0 ? (
                        <Box>
                            {filteredMonthlyTargets.map((target) => {
                                const activities = getActivitiesForMonth(target.id);
                                const isExpanded = expandedMonthId === target.id;
                                // Support both camelCase and snake_case for values
                                const dvCount = target.dvCount ?? target.dv_count ?? 0;
                                const dvDoneCount = target.dvDoneCount ?? target.dv_done_count ?? 0;
                                const fvCount = target.fvCount ?? target.fv_count ?? 0;
                                const fvDoneCount = target.fvDoneCount ?? target.fv_done_count ?? 0;
                                const fdCount = target.fdCount ?? target.fd_count ?? 0;
                                const fdDoneCount = target.fdDoneCount ?? target.fd_done_count ?? 0;
                                return (
                                    <Paper
                                        key={target.id}
                                        elevation={0}
                                        sx={{
                                            mb: 2,
                                            borderRadius: 3,
                                            border: '1px solid #E5E7EB',
                                            overflow: 'hidden'
                                        }}
                                    >
                                        {/* Monthly Target Row */}
                                        {isXs ? (
                                            <Box sx={{ p: 1.5, minWidth: 200 }}>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                        <span style={{ color: '#374151' }}>Month ID:</span> {target.id}
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        <span style={{ color: '#374151' }}>Month:</span> {target.month_year || target.monthYear}
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        <span style={{ color: '#374151' }}>Dealer Visit:</span> {dvDoneCount}/{dvCount}
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        <span style={{ color: '#374151' }}>Field Visit:</span> {fvDoneCount}/{fvCount}
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        <span style={{ color: '#374151' }}>Field Day:</span> {fdDoneCount}/{fdCount}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5, alignItems: 'center', mt: 1.5 }}>
                                                    {activities.length > 0 && (
                                                        <Button
                                                            variant="outlined"
                                                            color="primary"
                                                            size="small"
                                                            sx={{ borderRadius: 2, fontSize: 11, py: 0.3, px: 1 }}
                                                            onClick={() => handleToggleActivities(target.id)}
                                                        >
                                                            {isExpanded ? 'Hide' : 'View'} Activities
                                                        </Button>
                                                    )}
                                                </Box>
                                            </Box>
                                        ) : (
                                            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 2, alignItems: 'center' }}>
                                                    <Box>
                                                        <Typography variant="caption" color="textSecondary">Month ID</Typography>
                                                        <Typography variant="body2" fontWeight={600}>{target.id}</Typography>
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="caption" color="textSecondary">Month</Typography>
                                                        <Typography variant="body2" fontWeight={600}>{target.month_year || target.monthYear}</Typography>
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="caption" color="textSecondary">Dealer Visit</Typography>
                                                        <Typography variant="body2" fontWeight={600}>{dvDoneCount}/{dvCount}</Typography>
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="caption" color="textSecondary">Field Visit</Typography>
                                                        <Typography variant="body2" fontWeight={600}>{fvDoneCount}/{fvCount}</Typography>
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="caption" color="textSecondary">Field Day</Typography>
                                                        <Typography variant="body2" fontWeight={600}>{fdDoneCount}/{fdCount}</Typography>
                                                    </Box>
                                                </Box>
                                                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5, alignItems: 'center' }}>
                                                    {activities.length > 0 && (
                                                        <Button
                                                            variant="outlined"
                                                            color="primary"
                                                            size="small"
                                                            sx={{ borderRadius: 2, fontSize: 11, py: 0.4, px: 1.5 }}
                                                            onClick={() => handleToggleActivities(target.id)}
                                                        >
                                                            {isExpanded ? 'Hide' : 'View'} Activities
                                                        </Button>
                                                    )}
                                                </Box>
                                            </Box>
                                        )}

                                        {/* Activities - Collapsible */}
                                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                            <Box sx={{ px: 2, pb: 2 }}>
                                                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#4B5563' }}>
                                                    Associated Activities
                                                </Typography>

                                                {activitiesLoading ? (
                                                    <Typography variant="body2" color="textSecondary" sx={{ py: 2, textAlign: 'center' }}>
                                                        Loading activities...
                                                    </Typography>
                                                ) : activities.length > 0 ? (
                                                    isXs ? (
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                            {activities.map((activity) => (
                                                                <Paper
                                                                    key={activity.id}
                                                                    elevation={0}
                                                                    sx={{
                                                                        border: '1px solid #E5E7EB',
                                                                        borderRadius: 2,
                                                                        p: 1.5,
                                                                        position: 'relative',
                                                                        minWidth: 200,
                                                                        minHeight: 110,
                                                                    }}
                                                                >
                                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                            <span style={{ color: '#374151' }}>Activity ID:</span> {activity.id}
                                                                        </Typography>
                                                                        <Typography variant="body2">
                                                                            <span style={{ color: '#374151' }}>Village ID:</span> {activity.villageId || activity.village_id}
                                                                        </Typography>
                                                                        <Typography variant="body2">
                                                                            <span style={{ color: '#374151' }}>Activity Name:</span> {activity.activityName || activity.activity_name}
                                                                        </Typography>
                                                                        <Typography variant="body2">
                                                                            <span style={{ color: '#374151' }}>Start Time:</span> {new Date(activity.startTime || activity.start_time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                                                                        </Typography>
                                                                        <Typography variant="body2">
                                                                            <span style={{ color: '#374151' }}>End Time:</span> {new Date(activity.endTime || activity.end_time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                                                                        </Typography>
                                                                        <Typography variant="body2">
                                                                            <span style={{ color: '#374151' }}>No. of Farmers:</span> {activity.noOfFarmers || activity.no_of_farmers}
                                                                        </Typography>
                                                                    </Box>
                                                                    <Box sx={{ position: 'absolute', bottom: 8, right: 8, display: 'flex', gap: 1 }}>
                                                                        <IconButton
                                                                            color="primary"
                                                                            size="small"
                                                                            onClick={() => handleOpenImages(activity.id)}
                                                                        >
                                                                            <ImageIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Box>
                                                                </Paper>
                                                            ))}
                                                        </Box>
                                                    ) : (
                                                        <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, overflow: 'auto' }}>
                                                            <Table size="small">
                                                                <TableHead sx={{ backgroundColor: '#F9FAFB' }}>
                                                                    <TableRow>
                                                                        <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Activity ID</TableCell>
                                                                        <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Village</TableCell>
                                                                        <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Activity Name</TableCell>
                                                                        <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Start Time</TableCell>
                                                                        <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>End Time</TableCell>
                                                                        <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>No. of Farmers</TableCell>
                                                                        <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Actions</TableCell>
                                                                    </TableRow>
                                                                </TableHead>
                                                                <TableBody>
                                                                    {activities.map((activity) => (
                                                                        <TableRow key={activity.id}>
                                                                            <TableCell>{activity.id}</TableCell>
                                                                            <TableCell>{activity?.village?.name || activity.villageId}</TableCell>
                                                                            <TableCell>{activity.activityName || activity.activity_name}</TableCell>
                                                                            <TableCell>{new Date(activity.startTime || activity.start_time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</TableCell>
                                                                            <TableCell>{new Date(activity.endTime || activity.end_time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</TableCell>
                                                                            <TableCell>{activity.noOfFarmers || activity.no_of_farmers}</TableCell>
                                                                            <TableCell>
                                                                                <IconButton
                                                                                    color="primary"
                                                                                    size="small"
                                                                                    onClick={() => handleOpenImages(activity.id)}
                                                                                >
                                                                                    <ImageIcon fontSize="small" />
                                                                                </IconButton>
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    ))}
                                                                </TableBody>
                                                            </Table>
                                                        </Paper>
                                                    )
                                                ) : (
                                                    <Typography variant="body2" color="textSecondary" sx={{ py: 2, textAlign: 'center' }}>
                                                        No activities found.
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Collapse>
                                    </Paper>
                                );
                            })}
                        </Box>
                    ) : (
                        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #E5E7EB', textAlign: 'center' }}>
                            <Typography color="textSecondary">No monthly targets found for this post-season activity.</Typography>
                        </Paper>
                    )}
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button variant="contained" onClick={onClose} sx={{ borderRadius: 2, textTransform: 'none' }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Image Dialog */}
            {openImageDialog && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        bgcolor: 'rgba(0,0,0,0.5)',
                        zIndex: 2001,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                    onClick={handleCloseImages}
                >
                    <Paper
                        elevation={3}
                        sx={{
                            p: 3,
                            borderRadius: 3,
                            minWidth: 320,
                            minHeight: 200,
                            maxWidth: '90vw',
                            maxHeight: '90vh',
                            overflowY: 'auto',
                            position: 'relative',
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        <IconButton
                            onClick={handleCloseImages}
                            sx={{ position: 'absolute', top: 8, right: 8 }}
                        >
                            <CloseIcon />
                        </IconButton>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                            Activity Images
                        </Typography>
                        {mediaFilesLoading ? (
                            <Typography>Loading images...</Typography>
                        ) : mediaFilesData && Array.isArray(mediaFilesData.data) && mediaFilesData.data.length > 0 ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                {mediaFilesData.data.map((file, idx) => (
                                    <Box key={idx} sx={{ width: 120, height: 120, borderRadius: 2, overflow: 'hidden', border: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#F9FAFB' }}>
                                        <img
                                            src={file.url || file.fileUrl || file.file_url}
                                            alt={file.name || `Activity Image ${idx + 1}`}
                                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                        />
                                    </Box>
                                ))}
                            </Box>
                        ) : (
                            <Typography color="textSecondary">No images found for this activity.</Typography>
                        )}
                    </Paper>
                </Box>
            )}
        </>
    );
};

PostActivityDetailsView.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    row: PropTypes.object,
};

export default PostActivityDetailsView;
